import { useDispatch, useSelector } from "react-redux";
import DynamicIcon from "../DynamicIcon";
import { selectSelectedWarehouse, selectWarehouseDetailLoading } from "../../redux/warehouse/warehouseSlice";
import { closeModal, setModal } from "../../redux/rightModalSlice";
import LoadingSection from "../LoadingSection";

export default function ViewWarehouse() {
    const dispatch = useDispatch();
    const warehouse = useSelector(selectSelectedWarehouse);
    const loading = useSelector(selectWarehouseDetailLoading);

    if (loading) {
        return <LoadingSection />;
    }

    if (!warehouse) {
        return (
            <div className="p-5">
                <p className="text-gray-500 text-sm">Nessun magazzino selezionato.</p>
                <button className="secondary mt-3" onClick={() => dispatch(closeModal())}>
                    <DynamicIcon iconClass="fas fa-arrow-left" /> Indietro
                </button>
            </div>
        );
    }

    const address = [warehouse.address, warehouse.postalCode, warehouse.city, warehouse.province ? `(${warehouse.province})` : '']
        .filter(Boolean).join(' ');
    const positionsCount = warehouse.positions?.length || 0;

    return (
        <div className="p-5">
            {/* Header */}
            <div className="flex items-center justify-between mb-5 pb-4 border-b border-gray-200">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                        <DynamicIcon iconClass="fa-solid fa-warehouse text-blue-600" />
                    </div>
                    <div>
                        <h2 className="text-lg font-bold text-gray-900 leading-tight">{warehouse.name}</h2>
                        {address && (
                            <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                                <DynamicIcon iconClass="fa-solid fa-location-dot text-gray-400" />
                                {address}
                            </p>
                        )}
                    </div>
                </div>
                <div className="flex gap-2">
                    <button
                        className="secondary"
                        onClick={() => dispatch(setModal({ mode: 'edit', id: warehouse._id, percentuageWidth: 45 }))}
                    >
                        <DynamicIcon iconClass="fas fa-pen" /> Modifica
                    </button>
                    <button
                        className="primary"
                        onClick={() => dispatch(setModal({ mode: 'products', id: warehouse._id, percentuageWidth: 80 }))}
                    >
                        <DynamicIcon iconClass="fas fa-boxes-stacked" /> Prodotti
                    </button>
                </div>
            </div>

            {/* Info */}
            <div className="space-y-4">
                {/* Dettagli */}
                <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Dettagli</p>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <p className="text-xs text-gray-500">Indirizzo</p>
                            <p className="text-sm font-medium text-gray-800">{address || "--"}</p>
                        </div>
                        <div>
                            <p className="text-xs text-gray-500">Posizioni</p>
                            <p className="text-sm font-medium text-gray-800">{positionsCount}</p>
                        </div>
                        {warehouse.creationDate && (
                            <div>
                                <p className="text-xs text-gray-500">Creato il</p>
                                <p className="text-sm font-medium text-gray-800">{new Date(warehouse.creationDate).toLocaleDateString()}</p>
                            </div>
                        )}
                        {warehouse.lastUpdateDate && (
                            <div>
                                <p className="text-xs text-gray-500">Ultimo aggiornamento</p>
                                <p className="text-sm font-medium text-gray-800">{new Date(warehouse.lastUpdateDate).toLocaleDateString()}</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Posizioni */}
                <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                        Posizioni ({positionsCount})
                    </p>
                    {positionsCount > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            {warehouse.positions.map((position, index) => (
                                <div key={index} className="flex items-center gap-2 bg-white rounded-md px-3 py-2 border border-gray-100">
                                    <div className="w-6 h-6 rounded bg-blue-50 flex items-center justify-center flex-shrink-0">
                                        <DynamicIcon iconClass="fa-solid fa-location-crosshairs text-blue-500 text-xs" />
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium text-gray-800 truncate">{position.name}</p>
                                        {position.code && (
                                            <p className="text-xs text-gray-500">{position.code}</p>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-sm text-gray-400">Nessuna posizione definita</p>
                    )}
                </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end pt-4 mt-4 border-t border-gray-200">
                <button className="secondary" onClick={() => dispatch(closeModal())}>
                    <DynamicIcon iconClass="fas fa-times" /> Chiudi
                </button>
            </div>
        </div>
    );
}
